import { ReferencedEntitiesPills } from "@/components/ReferencedEntitiesPills";
import { cn } from "@/utils";
import { FlowSessionWorkItem, Message } from "@cohesive/api-client";
import { Entity, RailStep } from "@cohesive/ui-components";
import { ChevronDown, Clock, CornerDownRight, Sparkles } from "lucide-react";
import { AnimatePresence, cubicBezier, motion } from "motion/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import HarnessChatBubble from "./HarnessChatBubble";
import { buildHarnessOutline } from "./harnessRailItems";
import { HarnessSegmentRow } from "./HarnessSegment";
import { findAnswerMessage, isTurnSettled } from "./harnessTurnState";

/**
 * One harness conversation turn: the user prompt, the activity rail, and the
 * answer. The rail is an outline — the agent's own narration lines are chapter
 * headers and the thoughts, tool runs and agent boards that follow each nest
 * under it (see buildHarnessOutline) — with strict motion discipline:
 *
 * - Expansion state is user-owned: a toggle the user made is never undone.
 * - Automatic transitions are one-way and once-only. A finished segment
 *   starts closed; the rail collapses once when the turn settles. Nothing
 *   auto-expands, and a session status flip never toggles anything.
 * - Assistant text is intermediary until the turn is over: while live every
 *   text is a segment header; only once the turn settles does its last text
 *   render as the answer bubble, and once promoted it never demotes.
 */
interface HarnessTurnViewProps {
  /** Messages sharing one `turn_id` (user first, then assistant rounds). */
  turn: Message[];
  entities: Map<string, Entity>;
  isLastTurn: boolean;
  /** Whether the flow session is idle (`ready`); part of the settled-turn
   *  predicate. Threaded down from the chat area rather than subscribed to
   *  here, so it costs no session subscription per turn. */
  isSessionReady?: boolean;
  /** The session's background work, joined to Agent launches on `call_id`. */
  workItems?: FlowSessionWorkItem[];
  /** AskUserQuestion answers by tool-call id, recovered from answer messages. */
  askAnswers?: Record<string, Record<string, string>>;
  /** The tool-call id of the ask currently shown in the composer panel. */
  activeAskCallId?: string;
  lastUserMessageId?: string;
  lastUserMessageRef?: React.RefObject<HTMLDivElement | null>;
}

function turnDurationMs(turn: Message[]): number | undefined {
  let start = Infinity;
  let end = -Infinity;
  for (const m of turn) {
    for (const ts of [m.created_at, m.updated_at, m.sent_at]) {
      const t = ts ? Date.parse(ts) : NaN;
      if (Number.isNaN(t)) continue;
      start = Math.min(start, t);
      end = Math.max(end, t);
    }
  }
  if (start === Infinity) return undefined;
  return Math.max(0, end - start);
}

function turnStartMs(turn: Message[]): number | undefined {
  let start = Infinity;
  for (const m of turn) {
    for (const ts of [m.created_at, m.updated_at, m.sent_at]) {
      const t = ts ? Date.parse(ts) : NaN;
      if (!Number.isNaN(t)) start = Math.min(start, t);
    }
  }
  return start === Infinity ? undefined : start;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

// Design system "ease-out" — cubic-bezier(0.2, 0, 0, 1).
const EASE_OUT = cubicBezier(0.2, 0, 0, 1);

const STEP_ENTER = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.2, ease: EASE_OUT },
};

// How often (ms) the rail re-reads a streaming turn. See useThrottledValue.
const TURN_THROTTLE_MS = 250;

/**
 * Per-turn disclosure state, module-scoped so it survives the turn view
 * remounting. The chat list remounts a turn whenever the message array's
 * identity changes — an infinite-scroll fetch of older history, the work-item
 * poll, a scroll that reflows the list — and a user-owned rail must not snap
 * shut when that happens. Keyed by turn id; unbounded like the tool-call
 * animate-once set, but turns per session are bounded.
 */
interface TurnExpansion {
  rootOpen?: boolean;
  rootTouched: boolean;
  autoCollapsed: boolean;
  segmentOverrides: Record<string, boolean>;
}
const expansionStore = new Map<string, TurnExpansion>();
function expansionFor(turnKey: string): TurnExpansion {
  let state = expansionStore.get(turnKey);
  if (!state) {
    state = { rootTouched: false, autoCollapsed: false, segmentOverrides: {} };
    expansionStore.set(turnKey, state);
  }
  return state;
}

// How long the settled-turn predicate must hold before a live turn is
// promoted to complete. A single websocket burst can leave the last row a
// settled text with the session momentarily idle; the promotion is one-way,
// so it waits out that window rather than risk promoting a narration line.
const SETTLE_HYSTERESIS_MS = 1000;

/**
 * Throttle a rapidly-changing value to at most one update per `windowMs`
 * (leading + trailing edge) so a burst of websocket events renders as one
 * step instead of every transient in-between state.
 */
function useThrottledValue<T>(value: T, windowMs: number): T {
  const [snapshot, setSnapshot] = useState(value);
  const lastUpdateRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const elapsed = Date.now() - lastUpdateRef.current;
    const commit = () => {
      lastUpdateRef.current = Date.now();
      setSnapshot(value);
    };
    if (elapsed >= windowMs) {
      commit();
    } else {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(commit, windowMs - elapsed);
    }
    return () => clearTimeout(timerRef.current);
  }, [value, windowMs]);

  return snapshot;
}

/**
 * A one-way latch with hysteresis: becomes true once `condition` has held for
 * `delayMs` (immediately if it holds at mount), and then stays true whatever
 * `condition` does next. Resets only when `resetKey` changes.
 */
function useLatched(condition: boolean, delayMs: number, resetKey: string) {
  const [latched, setLatched] = useState(condition);
  const keyRef = useRef(resetKey);
  if (keyRef.current !== resetKey) {
    keyRef.current = resetKey;
    setLatched(condition);
  }
  useEffect(() => {
    if (latched || !condition) return;
    if (delayMs <= 0) {
      setLatched(true);
      return;
    }
    const id = setTimeout(() => setLatched(true), delayMs);
    return () => clearTimeout(id);
  }, [condition, delayMs, latched]);
  return latched;
}

export function HarnessTurnView({
  turn: rawTurn,
  entities,
  isLastTurn,
  isSessionReady = false,
  workItems,
  askAnswers,
  activeAskCallId,
  lastUserMessageId,
  lastUserMessageRef,
}: HarnessTurnViewProps) {
  const turn = useThrottledValue(rawTurn, TURN_THROTTLE_MS);
  // The API turn_id is constant for the exchange; the first message id can
  // shift when the list refetches, so key durable state on turn_id.
  const turnKey = turn.find((m) => m.turn_id)?.turn_id ?? turn[0]?.id ?? "";

  const userMsg = turn.find((m) => m.origin === "user");
  const assistantMsgs = useMemo(
    () =>
      turn.filter(
        (m) => m.origin === "assistant" || m.origin === "assistant-tool-call",
      ),
    [turn],
  );
  const skills = userMsg?.auto_selected_skills ?? [];

  // Genuinely complete: settled by the data (see isTurnSettled), held for the
  // hysteresis window on the live turn, then latched. Past turns are complete
  // by definition. Everything that used to flip with the session status —
  // the rail's open state, the working tail, which text is the answer — keys
  // off this latch, so a ready↔processing flip cannot move any of it.
  const settledNow = !isLastTurn || isTurnSettled(turn, { isSessionReady });
  const turnComplete = useLatched(
    settledNow,
    isLastTurn ? SETTLE_HYSTERESIS_MS : 0,
    turnKey,
  );
  const live = isLastTurn && !turnComplete;

  // The answer is promoted exactly once: the last plain-assistant text at the
  // moment the turn settles. Remembered so later rows can never demote it.
  const answerIdRef = useRef<string | undefined>(undefined);
  const answerMsg = useMemo(() => {
    if (answerIdRef.current) {
      const kept = turn.find((m) => m.id === answerIdRef.current);
      if (kept) return kept;
    }
    if (!turnComplete) return undefined;
    const found = findAnswerMessage(turn);
    answerIdRef.current = found?.id;
    return found;
  }, [turn, turnComplete]);
  // Only a promotion that happens after mount animates; history paints still.
  const promotedLiveRef = useRef(false);
  const mountedCompleteRef = useRef(turnComplete);
  if (turnComplete && !mountedCompleteRef.current)
    promotedLiveRef.current = true;

  const outline = useMemo(
    () =>
      buildHarnessOutline(assistantMsgs, {
        answerMessageId: answerMsg?.id,
        workItems,
        askAnswers,
        activeAskCallId,
      }),
    [assistantMsgs, answerMsg, workItems, askAnswers, activeAskCallId],
  );
  const { segments } = outline;
  const itemCount = segments.reduce((n, s) => n + s.items.length, 0);
  const currentSegment = segments[segments.length - 1];
  const liveItemKey = live
    ? currentSegment?.items[currentSegment.items.length - 1]?.key
    : undefined;

  // Live timer for the header.
  const startMs = useMemo(() => turnStartMs(turn), [turn]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [live]);

  const planning = live && itemCount === 0 && segments.length === 0;

  const summary = useMemo(() => {
    if (planning) return "Planning out work…";
    const parts: string[] = [];
    if (outline.toolCalls) {
      parts.push(
        `${outline.toolCalls} tool call${outline.toolCalls === 1 ? "" : "s"}`,
      );
    }
    if (outline.agents) {
      parts.push(`${outline.agents} agent${outline.agents === 1 ? "" : "s"}`);
    }
    if (!parts.length) {
      const steps = itemCount + segments.filter((s) => s.header).length;
      parts.push(`${steps} step${steps === 1 ? "" : "s"}`);
    }
    const ms = live
      ? startMs != null
        ? Math.max(0, nowMs - startMs)
        : undefined
      : turnDurationMs(turn);
    if (ms != null) {
      parts.push(
        `${live ? "working for" : "worked for"} ${formatDuration(ms)}`,
      );
    }
    return parts.join(" · ");
  }, [planning, outline, itemCount, segments, live, startMs, nowMs, turn]);

  // Root disclosure: open while live, collapsed once when the turn settles —
  // unless the user has touched it, after which it is theirs. State is
  // remembered per turn in a module store (see expansionStore) so the chat
  // list remounting the turn — an infinite-scroll fetch, a work-item poll —
  // can never snap a user-opened rail shut.
  const store = expansionFor(turnKey);
  const [rootOpen, setRootOpenState] = useState(() => store.rootOpen ?? live);
  const setRootOpen = useCallback(
    (next: boolean) => {
      store.rootOpen = next;
      setRootOpenState(next);
    },
    [store],
  );
  // One-way, once-only auto-collapse: when the turn settles, collapse the rail
  // a single time, and never if the user has already touched it.
  useEffect(() => {
    if (turnComplete && !store.rootTouched && !store.autoCollapsed) {
      store.autoCollapsed = true;
      setRootOpen(false);
    }
  }, [turnComplete, store, setRootOpen]);
  const toggleRoot = useCallback(() => {
    store.rootTouched = true;
    setRootOpen(!store.rootOpen);
  }, [store, setRootOpen]);

  // Segment disclosure: the current segment of a live turn is open by
  // default, every other one closed; a user toggle overrides for good and is
  // likewise remembered across remounts.
  const [, forceSegmentRerender] = useState(0);
  const isSegmentOpen = (key: string, current: boolean) =>
    store.segmentOverrides[key] ?? (current && live);
  const toggleSegment = (key: string, current: boolean) => {
    store.segmentOverrides[key] = !(
      store.segmentOverrides[key] ??
      (current && live)
    );
    forceSegmentRerender((n) => n + 1);
  };

  const hasRail = segments.length > 0 || live;
  const canToggle = segments.length > 0;
  const showLine = rootOpen && segments.length > 0;
  const showWorkingTail = live && (itemCount > 0 || segments.length > 0);

  const lastTurnMsg = turn[turn.length - 1];
  // The active AskUserQuestion is answered in the composer panel; the rail's
  // live line reads as waiting on it. Any other pending confirmation keeps the
  // generic "waiting for user input" heartbeat.
  const awaitingAsk =
    !!activeAskCallId &&
    turn.some((m) => m.tool_calls?.some((tc) => tc.id === activeAskCallId));
  const awaitingApproval =
    !awaitingAsk &&
    lastTurnMsg?.origin === "assistant-tool-call" &&
    (lastTurnMsg.tool_calls?.some(
      (tc) => tc.user_confirmation_status === "pending",
    ) ??
      false);

  return (
    <div>
      {/* A turn with no user message is a follow-up: background work the
          previous turn left running, reporting back under its own turn_id. */}
      {!userMsg && (
        <div className="flex items-center gap-2 px-4 pt-6 pb-1 font-sans text-xs text-n-50">
          <CornerDownRight className="size-3.5" />
          Follow-up
        </div>
      )}
      {userMsg && (
        <HarnessChatBubble
          message={userMsg}
          entities={entities}
          ref={
            userMsg.id === lastUserMessageId ? lastUserMessageRef : undefined
          }
        >
          {userMsg.referenced_entities &&
            userMsg.referenced_entities.length > 0 && (
              <div className="w-full flex justify-end mt-1">
                <ReferencedEntitiesPills
                  entities={userMsg.referenced_entities}
                  className="px-0 py-0 max-w-[90%] justify-end flex-wrap"
                />
              </div>
            )}
        </HarnessChatBubble>
      )}

      {hasRail && (
        <div
          className="relative"
          data-testid="harness-rail"
          data-open={rootOpen}
        >
          {showLine && (
            <span
              aria-hidden
              className="pointer-events-none absolute left-[27px] top-5 bottom-5 w-px bg-n-20"
            />
          )}

          <button
            type="button"
            onClick={canToggle ? toggleRoot : undefined}
            aria-expanded={canToggle ? rootOpen : undefined}
            disabled={!canToggle}
            data-testid="harness-rail-header"
            className={cn(
              "group flex w-full items-center gap-2 px-4 py-2 text-left outline-none",
              !canToggle && "cursor-default",
            )}
          >
            <span className="relative z-[1] inline-flex size-6 shrink-0 items-center justify-center bg-background text-n-80">
              <Clock className="size-[18px]" />
            </span>
            <span
              className={cn(
                "min-w-0 flex-1 truncate font-sans text-sm text-n-60 transition-colors duration-150 ease-out",
                canToggle && "group-hover:text-n-80",
                planning && "shimmer",
              )}
            >
              {summary}
            </span>
            {canToggle && (
              <ChevronDown
                className={cn(
                  "size-3.5 shrink-0 text-n-50 transition-all duration-150 ease-out group-hover:text-n-70",
                  rootOpen && "rotate-180",
                )}
              />
            )}
          </button>

          <AnimatePresence initial={false}>
            {rootOpen && (
              <motion.div
                key="cot-body"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                {skills.length > 0 && (
                  <div className="px-4 py-2">
                    {skills.map((skill) => (
                      <motion.div key={skill.id} {...STEP_ENTER}>
                        <RailStep glyph={Sparkles} tone="tinted" last>
                          <div className="pt-1 font-sans text-sm text-n-70">
                            Using skill:{" "}
                            <span className="font-medium text-n-90">
                              {skill.name}
                            </span>
                          </div>
                        </RailStep>
                      </motion.div>
                    ))}
                  </div>
                )}

                {segments.map((segment, i) => {
                  const current = i === segments.length - 1;
                  return (
                    <HarnessSegmentRow
                      key={segment.key}
                      segment={segment}
                      current={current && live}
                      open={isSegmentOpen(segment.key, current)}
                      liveItemKey={liveItemKey}
                      onToggle={() => toggleSegment(segment.key, current)}
                    />
                  );
                })}

                <AnimatePresence initial={false}>
                  {showWorkingTail && (
                    <motion.div
                      key="cot-working-tail"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                      className="px-4 py-2"
                    >
                      <RailStep
                        icon={
                          <span className="relative flex size-2.5 items-center justify-center">
                            <span
                              className={cn(
                                "absolute inline-flex size-2.5 animate-ping rounded-full motion-reduce:animate-none",
                                awaitingApproval || awaitingAsk
                                  ? "bg-lc-waiting [animation-duration:2.6s]"
                                  : "bg-rust",
                              )}
                            />
                            <span
                              className={cn(
                                "relative inline-flex size-2 rounded-full",
                                awaitingApproval || awaitingAsk
                                  ? "bg-lc-waiting"
                                  : "bg-rust",
                              )}
                            />
                          </span>
                        }
                        last
                      >
                        <span
                          className="shimmer -ml-1 font-sans text-sm text-n-60"
                          style={
                            awaitingApproval || awaitingAsk
                              ? ({
                                  "--shimmer-duration": "3s",
                                  "--shimmer-bg-color":
                                    "color-mix(in oklab, var(--lc-waiting) 65%, var(--n-40))",
                                  "--shimmer-highlight-color":
                                    "var(--lc-waiting)",
                                } as React.CSSProperties)
                              : undefined
                          }
                        >
                          {awaitingAsk
                            ? "Waiting for your answer…"
                            : awaitingApproval
                              ? "Waiting for user input…"
                              : "Working…"}
                        </span>
                      </RailStep>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* The answer, promoted once the turn settled; a live promotion eases
          in, history paints still. */}
      {answerMsg && (
        <motion.div
          key={answerMsg.id}
          data-testid="harness-answer"
          initial={promotedLiveRef.current ? { opacity: 0, y: 6 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: EASE_OUT }}
        >
          <HarnessChatBubble message={answerMsg} entities={entities} />
        </motion.div>
      )}
    </div>
  );
}
